import { cx } from "@jugl-web/utils";
import { FC, useEffect, useMemo, useState } from "react";

interface ProgressMessagesProps {
  messages: string[];
  messageDurationMs?: number;
  dotsIntervalMs?: number;
  loop?: boolean;
  className?: string;
}

const DEFAULT_MESSAGE_DURATION_MS = 2200;
const DEFAULT_DOTS_INTERVAL_MS = 450;
const DOT_STATES = ["", ".", "..", "..."] as const;

export const ProgressMessages: FC<ProgressMessagesProps> = ({
  messages,
  messageDurationMs = DEFAULT_MESSAGE_DURATION_MS,
  dotsIntervalMs = DEFAULT_DOTS_INTERVAL_MS,
  loop = true,
  className,
}) => {
  const safeMessages = useMemo(
    () => messages.filter((message) => message.trim().length > 0),
    [messages]
  );

  const [messageIndex, setMessageIndex] = useState(0);
  const [dotIndex, setDotIndex] = useState(0);

  useEffect(() => {
    setMessageIndex(0);
    setDotIndex(0);
  }, [safeMessages]);

  useEffect(() => {
    if (safeMessages.length <= 1) {
      return undefined;
    }

    if (messageIndex >= safeMessages.length - 1 && !loop) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setMessageIndex((currentIndex) =>
        currentIndex >= safeMessages.length - 1 ? 0 : currentIndex + 1
      );
    }, messageDurationMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loop, messageDurationMs, messageIndex, safeMessages.length]);

  useEffect(() => {
    if (safeMessages.length === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setDotIndex((current) => (current + 1) % DOT_STATES.length);
    }, dotsIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dotsIntervalMs, safeMessages.length]);

  if (safeMessages.length === 0) {
    return null;
  }

  return (
    <>
      <style>
        {`
            @keyframes progress-messages-fade-up {
              from {
                opacity: 0;
                transform: translateY(4px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
  
            .progress-messages__text {
              animation: progress-messages-fade-up 240ms ease-out;
            }
          `}
      </style>
      <div
        className={cx(
          "inline-flex items-baseline whitespace-nowrap",
          className
        )}
      >
        <span key={messageIndex} className="progress-messages__text">
          {safeMessages[messageIndex]}
        </span>
        <span
          key={`${messageIndex}-${dotIndex}`}
          aria-hidden="true"
          className="inline-block w-[3ch] overflow-hidden text-left"
        >
          {DOT_STATES[dotIndex]}
        </span>
      </div>
    </>
  );
};
